import type { VoiceoverPlanTone } from './planner-schema'

export interface ParsedAssistantPromptHints {
  durationSeconds: number
  aspectRatio: '9:16' | '16:9' | '1:1' | '4:5'
  tone: VoiceoverPlanTone
  needsBgm: boolean
  exportDraft: boolean
  timelineIntent: 'reuse-current' | 'create-draft' | 'preserve-current'
}

function extractDurationSeconds(prompt: string): number {
  const match = prompt.match(/(\d{1,3})\s*(秒|s|sec|secs|second|seconds)/i)
  const duration = Number(match?.[1] ?? 30)
  return Math.min(Math.max(duration, 8), 180)
}

function detectAspectRatio(prompt: string): ParsedAssistantPromptHints['aspectRatio'] {
  if (/4\s*[:：/]\s*5|4比5|竖向海报/i.test(prompt)) {
    return '4:5'
  }
  if (/1\s*[:：/]\s*1|1比1|方形/i.test(prompt)) {
    return '1:1'
  }
  if (/16\s*[:：/]\s*9|16比9|横版/i.test(prompt)) {
    return '16:9'
  }
  return '9:16'
}

function detectTone(prompt: string): VoiceoverPlanTone {
  if (/专业|正式|严肃/i.test(prompt)) {
    return 'professional'
  }
  if (/轻松|活泼|亲切|友好/i.test(prompt)) {
    return 'friendly'
  }
  return 'natural'
}

function detectNeedsBgm(prompt: string): boolean {
  if (/不要背景音乐|无背景音乐|纯人声/i.test(prompt)) {
    return false
  }
  if (/背景音乐|bgm|配乐/i.test(prompt)) {
    return true
  }
  return true
}

function detectExportDraft(prompt: string): boolean {
  if (/不要导出|先不要导出|不用导出|暂不导出|先别导出/i.test(prompt)) {
    return false
  }
  return /导出|成片|导出来|直接出片/i.test(prompt)
}

function detectTimelineIntent(prompt: string): ParsedAssistantPromptHints['timelineIntent'] {
  if (/不要动当前项目|不要修改当前项目|保留当前项目|另存为|复制一份|新建草稿|新项目里做|不要覆盖当前时间线/i.test(prompt)) {
    return /新建草稿|新项目里做|另存为|复制一份/i.test(prompt)
      ? 'create-draft'
      : 'preserve-current'
  }

  return 'reuse-current'
}

export function parseAssistantPrompt(prompt: string): ParsedAssistantPromptHints {
  return {
    durationSeconds: extractDurationSeconds(prompt),
    aspectRatio: detectAspectRatio(prompt),
    tone: detectTone(prompt),
    needsBgm: detectNeedsBgm(prompt),
    exportDraft: detectExportDraft(prompt),
    timelineIntent: detectTimelineIntent(prompt),
  }
}
